import { Link } from "react-router-dom";
import { Phone, Mail, MessageCircle, ArrowUpRight, Download, Linkedin } from "lucide-react";
import { NAV, SERVICES, CONTACT, CATALOGUE_URL, LINKEDIN_URL, waLink } from "../lib/site";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-navy text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-20">
        <p className="font-display font-extrabold tracking-tight leading-none text-[13vw] sm:text-7xl lg:text-8xl text-stroke-light select-none">
          Supply. Source. Run.
        </p>

        <div className="grid md:grid-cols-12 gap-10 py-14 border-b border-white/10 mt-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <img src="/logo192.png" alt="Saarthva logo" className="w-11 h-11 rounded-xl" />
              <span className="leading-none">
                <span className="block font-display font-extrabold tracking-tight text-lg">SAARTHVA</span>
                <span className="block text-[9px] font-semibold tracking-[0.32em] text-saffron">VENTURES</span>
              </span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs">
              Your Business Saarthi — one accountable partner for procurement, sourcing and business operations.
            </p>
            <Link
              to="/request-quote"
              data-testid="footer-request-quote-link"
              className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold text-saffron hover:text-white transition-colors"
            >
              Request a Quote <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="md:col-span-2">
            <p className="font-mono text-[11px] tracking-widest text-slate-500 mb-4">EXPLORE</p>
            <ul className="space-y-2.5">
              {NAV.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    data-testid={`footer-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="text-sm text-slate-300 hover:text-white transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-[11px] tracking-widest text-slate-500 mb-4">SOLUTIONS</p>
            <ul className="space-y-2.5">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/products#${s.id}`}
                    data-testid={`footer-solution-${s.id}`}
                    className="text-sm text-slate-300 hover:text-white transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-[11px] tracking-widest text-slate-500 mb-4">TALK TO US</p>
            <div className="space-y-3">
              <a href={`tel:+${CONTACT.phoneIntl}`} data-testid="footer-phone-link" className="flex items-center gap-3 text-sm text-slate-300 hover:text-white transition-colors">
                <Phone className="w-4 h-4 text-saffron" /> {CONTACT.phoneDisplay}
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email-link" className="flex items-center gap-3 text-sm text-slate-300 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-saffron" /> {CONTACT.email}
              </a>
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin-link" className="flex items-center gap-3 text-sm text-slate-300 hover:text-white transition-colors">
                <Linkedin className="w-4 h-4 text-saffron" /> Saarthva on LinkedIn
              </a>
              <a
                href={waLink("Hi Saarthva, I'd like to discuss a requirement for my business.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-link"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-saffron px-4 py-2 text-sm font-semibold transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
              </a>
              <p className="text-xs text-slate-500 pt-2">{CONTACT.hours}</p>
              <a href={CATALOGUE_URL} data-testid="footer-catalogue-download" className="flex items-center gap-3 text-sm text-slate-300 hover:text-white transition-colors pt-1">
                <Download className="w-4 h-4 text-saffron" /> Download Line Card (PDF)
              </a>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 py-5 text-xs text-slate-400" data-testid="footer-policy-links">
          <Link to="/faq" data-testid="footer-faq-link" className="hover:text-white transition-colors">FAQs</Link>
          <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white transition-colors">Terms & Conditions</Link>
          <Link to="/shipping-delivery" data-testid="footer-shipping-link" className="hover:text-white transition-colors">Shipping / Delivery Policy</Link>
          <Link to="/returns-replacement" data-testid="footer-returns-link" className="hover:text-white transition-colors">Returns / Replacement Policy</Link>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-6 text-xs text-slate-500 border-t border-white/10">
          <p>© {new Date().getFullYear()} Saarthva Ventures. All rights reserved.</p>
          <p className="font-mono tracking-widest">MADE IN INDIA · SUPPLY. SOURCE. RUN.</p>
          <Link to="/admin" data-testid="footer-admin-link" className="hover:text-slate-300 transition-colors">
            Engagements
          </Link>
        </div>
      </div>
    </footer>
  );
}
